import { Link } from "react-router-dom";
import { ArrowRight, Calendar, ShieldCheck, Package, Globe,
         Beaker, Dumbbell, UtensilsCrossed, Building2, ShoppingBag, Users2,
         Droplets, Layers, TrendingUp, Boxes, Handshake, CheckCircle2,
         Palette, Zap, Truck, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import PublicLayout from "@/components/layout/PublicLayout";
import { INDUSTRIES, HOW_IT_WORKS, FAQS, TESTIMONIALS, BRAND, CONSULTATION_URL, resolveFaqAnswer, MOQ_DESCRIPTION_DEFAULT } from "@/data/constants";
import api from "@/lib/api";
import useSEO from "@/hooks/useSEO";
import { PRICING_TIERS, PricingCard } from "@/pages/Pricing";
import HomeClientShowcase from "@/components/HomeClientShowcase";
import HomeTurnkeyFeatured from "@/components/HomeTurnkeyFeatured";

const TRUSTED_BY = [
  { label: "Beverage Startups", Icon: Beaker },
  { label: "Wellness & Fitness", Icon: Dumbbell },
  { label: "Restaurants & Cafés", Icon: UtensilsCrossed },
  { label: "Hotels & Resorts", Icon: Building2 },
  { label: "Retail & E-Commerce", Icon: ShoppingBag },
  { label: "Corporate & Nonprofits", Icon: Users2 },
];

const Home = () => {
  useSEO({
    title: "FLOWTALIS™ — Premium Private-Label Beverage Manufacturing",
    description: "The private-label beverage platform engineered for premium brands. Formulation, packaging, production, artwork, and shipping in one partner.",
    canonical: "https://flowtalis.com/",
  });
  const [products, setProducts] = useState([]);
  const [moqDescription, setMoqDescription] = useState(MOQ_DESCRIPTION_DEFAULT);
  useEffect(() => {
    api.get("/products").then((r) => {
      setProducts(r.data.products);
      if (r.data.moq_description) setMoqDescription(r.data.moq_description);
    }).catch(() => {});
  }, []);

  return (
    <PublicLayout>
      {/* ============================================================
          HERO — Split, white, bottles on the right
          ============================================================ */}
      <section className="bg-white relative overflow-hidden" data-testid="home-hero">
        <div className="ft-container pt-14 pb-8 lg:pt-20 lg:pb-14 grid lg:grid-cols-12 gap-6 lg:gap-8 items-start">
          {/* LEFT — content */}
          <div className="lg:col-span-5 ft-fade-in lg:pt-2">
            <div className="text-[0.6875rem] uppercase tracking-[0.42em] font-semibold" style={{ color: "var(--ft-gold)" }}>
              From Source to Bottle™
            </div>
            <h1 className="mt-6 text-[clamp(2.5rem,5vw,4.25rem)] font-bold leading-[1.02] tracking-[-0.03em] text-[var(--ft-black)]">
              The private-label<br/>
              beverage platform<br/>
              <span className="ft-serif font-medium" style={{ color: "var(--ft-gold)" }}>engineered for premium brands.</span>
            </h1>
            <p className="mt-8 text-[1.0625rem] leading-[1.7] text-[var(--ft-charcoal)] max-w-xl">
              FLOWTALIS™ manages every stage of your beverage launch — formulation, packaging, production,
              artwork, and shipping — inside one enterprise-grade operating system.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a href={CONSULTATION_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-book-consultation" className="ft-btn-gold">
                Book a Consultation <ArrowRight size={16} />
              </a>
              <Link
                to="/products"
                data-testid="hero-view-products"
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-sm font-semibold border-2 border-[var(--ft-gold)] text-[var(--ft-gold)] hover:bg-[var(--ft-gold)] hover:text-white transition-all"
              >
                View Product Categories
              </Link>
            </div>
          </div>

          {/* RIGHT — Approved FLOWTALIS Hero Bottle Showcase asset (30-40% larger, floating) */}
          <div className="lg:col-span-7 relative flex items-start justify-center lg:justify-end lg:-mt-4 xl:-mt-6">
            <div className="relative w-full max-w-[820px]">
              <picture>
                <source
                  type="image/png"
                  srcSet={`${BRAND.heroBottleShowcase} 1x, ${BRAND.heroBottleShowcase} 2x`}
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 60vw, 820px"
                />
                <img
                  src={BRAND.heroBottleShowcase}
                  srcSet={`${BRAND.heroBottleShowcase} 1x, ${BRAND.heroBottleShowcase} 2x`}
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 60vw, 820px"
                  alt="FLOWTALIS premium bottle showcase — Hydrate H2O, Mode Hydration, Your Brand"
                  data-testid="hero-bottle-showcase-image"
                  className="w-full h-auto object-contain select-none ft-hero-float"
                  draggable="false"
                  loading="eager"
                  fetchPriority="high"
                  decoding="sync"
                  width="820"
                  height="820"
                />
              </picture>
              {/* Subtle studio ground shadow */}
              <div
                aria-hidden
                className="absolute left-1/2 -translate-x-1/2 bottom-[2%] w-[62%] h-[18px] rounded-[50%] pointer-events-none"
                style={{
                  background: "radial-gradient(ellipse at center, rgba(17,17,17,0.16) 0%, rgba(17,17,17,0.08) 40%, transparent 75%)",
                  filter: "blur(10px)",
                }}
              />
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="ft-container pb-16">
          <div className="rounded-2xl border border-[var(--ft-border)] bg-[var(--ft-light-gray)] p-6 lg:p-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8">
              {[
                { Icon: Calendar, value: "6–14", label: "Week Launches" },
                { Icon: ShieldCheck, value: "FDA", label: "Compliant Partners" },
                { Icon: Package, value: "1 Pallet", label: "Low MOQ Start" },
                { Icon: Globe, value: "GLOBAL", label: "Manufacturing Network" },
              ].map(({ Icon, value, label }) => (
                <div key={label} className="flex items-center gap-4">
                  <Icon size={30} strokeWidth={1.25} style={{ color: "var(--ft-gold)" }} className="flex-shrink-0" />
                  <div>
                    <div className="text-2xl font-bold text-[var(--ft-black)] leading-none tracking-tight">{value}</div>
                    <div className="text-[0.6875rem] uppercase tracking-[0.24em] text-[var(--ft-muted)] mt-1.5 font-semibold">{label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          TRUSTED BY
          ============================================================ */}
      <section className="pb-20 pt-4 bg-white border-b border-[var(--ft-border)]" data-testid="home-trusted">
        <div className="ft-container">
          <div className="text-center text-[0.6875rem] uppercase tracking-[0.32em] font-semibold mb-10" style={{ color: "var(--ft-gold)" }}>
            Trusted by visionaries across industries
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-8 gap-x-4">
            {TRUSTED_BY.map(({ label, Icon }) => (
              <div key={label} className="flex flex-col md:flex-row items-center justify-center gap-3 text-center md:text-left">
                <Icon size={24} strokeWidth={1.25} style={{ color: "var(--ft-gold)" }} />
                <div className="text-[0.75rem] uppercase tracking-[0.14em] font-semibold text-[var(--ft-charcoal)] leading-tight">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          INDUSTRIES WE SERVE
          ============================================================ */}
      <section className="py-28 bg-white" data-testid="home-industries">
        <div className="ft-container">
          <div className="max-w-2xl mb-16">
            <div className="ft-eyebrow mb-5">Industries We Serve</div>
            <span className="ft-gold-rule-lg" />
            <h2 className="mt-6 text-[clamp(1.875rem,3.75vw,3rem)] font-bold leading-[1.05] tracking-tight text-[var(--ft-black)]">
              Built for brands that <span className="ft-serif font-medium" style={{ color: "var(--ft-gold)" }}>demand precision</span>.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {INDUSTRIES.map((ind) => (
              <div key={ind.name} className="ft-card">
                <h3 className="font-semibold text-[var(--ft-black)]">{ind.name}</h3>
                <div className="ft-gold-rule my-3" />
                <p className="text-sm text-[var(--ft-muted)] leading-relaxed">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          PRODUCT CATEGORIES
          ============================================================ */}
      <section className="py-28 bg-[var(--ft-light-gray)]" data-testid="home-products">
        <div className="ft-container">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="ft-eyebrow mb-5">Product Categories</div>
              <span className="ft-gold-rule-lg" />
              <h2 className="mt-6 text-[clamp(1.875rem,3.75vw,3rem)] font-bold leading-[1.05] tracking-tight text-[var(--ft-black)]">
                Six premium categories.<br/>
                <span className="ft-serif font-medium" style={{ color: "var(--ft-gold)" }}>Infinite</span> brand possibilities.
              </h2>
            </div>
            <Link to="/products" className="ft-btn-secondary" data-testid="home-all-products">
              All Products <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {products.slice(0, 6).map((p) => (
              <Link key={p.slug} to={`/products/${p.slug}`} className="ft-card block group" data-testid={`home-product-${p.slug}`}>
                <div className="flex items-center justify-between">
                  <div className="ft-badge">{p.min_order}</div>
                  <ArrowRight size={18} strokeWidth={1.5} className="opacity-30 group-hover:opacity-100 group-hover:translate-x-1 transition-all" style={{ color: "var(--ft-gold)" }} />
                </div>
                <h3 className="mt-6 text-xl font-semibold text-[var(--ft-black)]">{p.name}</h3>
                <p className="mt-2 text-sm text-[var(--ft-muted)] leading-relaxed">{p.tagline}</p>
                <div className="mt-6 pt-4 border-t border-[var(--ft-border)] text-[0.625rem] text-[var(--ft-muted)] uppercase tracking-[0.24em] font-semibold">{p.timeline}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          HOW IT WORKS
          ============================================================ */}
      <section className="py-28 bg-white" data-testid="home-how">
        <div className="ft-container">
          <div className="max-w-2xl mb-16">
            <div className="ft-eyebrow mb-5">The FLOWTALIS Process</div>
            <span className="ft-gold-rule-lg" />
            <h2 className="mt-6 text-[clamp(1.875rem,3.75vw,3rem)] font-bold leading-[1.05] tracking-tight text-[var(--ft-black)]">
              A structured launch, <span className="ft-serif font-medium" style={{ color: "var(--ft-gold)" }}>from first call</span> to reorder.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {HOW_IT_WORKS.map((s) => (
              <div key={s.step} className="ft-card">
                <div className="text-[0.625rem] uppercase tracking-[0.32em] font-bold" style={{ color: "var(--ft-gold)" }}>STEP {s.step}</div>
                <h3 className="mt-4 text-lg font-semibold text-[var(--ft-black)]">{s.title}</h3>
                <div className="ft-gold-rule my-3" />
                <p className="text-sm text-[var(--ft-muted)] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          WHY FLOWTALIS
          ============================================================ */}
      <section className="py-28 bg-[var(--ft-light-gray)]" data-testid="home-value">
        <div className="ft-container">
          <div className="max-w-2xl mb-16">
            <div className="ft-eyebrow mb-5">Why FLOWTALIS</div>
            <span className="ft-gold-rule-lg" />
            <h2 className="mt-6 text-[clamp(1.875rem,3.75vw,3rem)] font-bold leading-[1.05] tracking-tight text-[var(--ft-black)]">
              Enterprise operations, executive polish, <span className="ft-serif font-medium" style={{ color: "var(--ft-gold)" }}>one team</span> from source to shelf.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5 ft-stagger">
            {[
              { icon: Droplets, title: "Premium categories", desc: "Purified, alkaline, spring, sparkling, CBD, and pet CBD water — with room to scale into functional beverages." },
              { icon: ShieldCheck, title: "Compliance built-in", desc: "FDA-compliant manufacturing, third-party COAs, and documented QA at every production run." },
              { icon: Layers, title: "One operating system", desc: "Artwork approvals, invoices, production updates, shipping, and reorders — all in one client portal." },
              { icon: TrendingUp, title: "Executive reporting", desc: "Real-time visibility into pipeline, revenue, and project performance for founders and operators." },
              { icon: Boxes, title: "Scalable manufacturing", desc: "A vetted network of production partners across the United States. International expansion on the roadmap." },
              { icon: Handshake, title: "White-glove account team", desc: "Dedicated brand, design, production, and customer success leads assigned to your launch." },
              { icon: Package, title: "Reliable supply, every order", desc: "We provide dependable manufacturing capacity to help keep your branded beverages available when your business needs them." },
              { icon: Palette, title: "Custom designs in 1–3 business days", desc: "Our design team creates professional bottle mockups for your review before production begins, helping you visualize your brand before production starts." },
              { icon: Zap, title: "Fast production & delivery", desc: "Once your artwork is approved, we move quickly into production and coordinate efficient order fulfillment." },
              { icon: Truck, title: "Serving customers nationwide", desc: "We proudly manufacture and ship private label beverages to businesses, organizations, and events throughout the United States." },
            ].map((f, i) => (
              <div key={i} className="ft-card" data-testid={`value-card-${i}`}>
                <f.icon size={22} strokeWidth={1.4} style={{ color: "var(--ft-gold)" }} />
                <h3 className="mt-5 text-lg font-semibold text-[var(--ft-black)]">{f.title}</h3>
                <p className="mt-2 text-sm text-[var(--ft-muted)] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          MANUFACTURING CAPABILITIES
          ============================================================ */}
      <section className="py-28 bg-white" data-testid="home-capabilities">
        <div className="ft-container grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="ft-eyebrow mb-5">Manufacturing Capabilities</div>
            <span className="ft-gold-rule-lg" />
            <h2 className="mt-6 text-[clamp(1.75rem,3.4vw,2.75rem)] font-bold leading-[1.05] tracking-tight text-[var(--ft-black)]">
              A vetted network. <span className="ft-serif font-medium" style={{ color: "var(--ft-gold)" }}>Executive-grade</span> production.
            </h2>
            <div className="mt-8 space-y-4">
              {[
                "Multi-stage reverse osmosis, ionization, and carbonation",
                "PET, aluminum, and glass bottle formats",
                "Custom caps, labels, shrink sleeves, and closures",
                "Third-party COA and compliance documentation",
                "Palletized shipping and warehousing coordination",
                "Dedicated project team assigned to every launch",
              ].map((line, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 size={18} strokeWidth={1.75} style={{ color: "var(--ft-gold)" }} className="mt-0.5 flex-shrink-0" />
                  <span className="text-[var(--ft-charcoal)]">{line}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden aspect-[4/5] md:aspect-[3/4] border border-[var(--ft-border)] relative"
               style={{ background: "linear-gradient(135deg, #0d0d0d 0%, #1a1a1a 55%, #0a0a0a 100%)" }}>
            <img
              src="https://customer-assets-39nsmqrw.emergentagent.net/job_beverage-platform/artifacts/fzdarkxp_Hydrate%20H2O.png"
              alt="Hydrate H2O — premium alkaline water bottle in a natural waterfall setting"
              className="w-full h-full object-contain"
              loading="lazy"
              decoding="async"
              onError={(e) => {
                e.currentTarget.style.display = "none";
                const ph = e.currentTarget.nextElementSibling;
                if (ph) ph.style.display = "flex";
              }}
            />
            {/* Graceful fallback if the stock image ever fails */}
            <div
              aria-hidden
              className="absolute inset-0 hidden items-center justify-center flex-col gap-3 text-center px-8"
              style={{ background: "linear-gradient(135deg, var(--ft-black) 0%, var(--ft-charcoal) 100%)" }}
            >
              <Boxes size={40} strokeWidth={1.25} style={{ color: "var(--ft-gold)" }} />
              <div className="text-[0.65rem] uppercase tracking-[0.3em] font-semibold" style={{ color: "var(--ft-gold)" }}>Manufacturing Network</div>
              <div className="text-white text-lg font-semibold leading-tight">Modern beverage bottling — <span className="ft-serif font-medium" style={{ color: "var(--ft-gold)" }}>coast to coast</span>.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          SUCCESS STORIES
          ============================================================ */}
      <section className="py-28 bg-[var(--ft-light-gray)]" data-testid="home-testimonials">
        <div className="ft-container">
          <div className="max-w-2xl mb-14">
            <div className="ft-eyebrow mb-5">Success Stories</div>
            <span className="ft-gold-rule-lg" />
            <h2 className="mt-6 text-[clamp(1.875rem,3.75vw,3rem)] font-bold leading-[1.05] tracking-tight text-[var(--ft-black)]">
              Founders and operators <span className="ft-serif font-medium" style={{ color: "var(--ft-gold)" }}>choose</span> FLOWTALIS.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="ft-card">
                <div className="ft-serif text-5xl leading-none" style={{ color: "var(--ft-gold)" }}>&ldquo;</div>
                <p className="mt-3 text-[15px] leading-relaxed text-[var(--ft-charcoal)]">{t.quote}</p>
                <div className="mt-6 pt-4 border-t border-[var(--ft-border)]">
                  <div className="font-semibold text-sm">{t.who}</div>
                  <div className="text-xs text-[var(--ft-muted)] mt-0.5">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          FAQ
          ============================================================ */}
      <section className="py-28 bg-white" data-testid="home-faq">
        <div className="ft-container max-w-3xl">
          <div className="ft-eyebrow mb-5">Frequently Asked Questions</div>
          <span className="ft-gold-rule-lg" />
          <h2 className="mt-6 mb-12 text-[clamp(1.875rem,3.75vw,3rem)] font-bold leading-[1.05] tracking-tight text-[var(--ft-black)]">
            Everything you need to <span className="ft-serif font-medium" style={{ color: "var(--ft-gold)" }}>know</span>.
          </h2>
          <div className="space-y-3">
            {FAQS.slice(0, 4).map((f, i) => (
              <details key={i} className="ft-card group" data-testid={`home-faq-${i}`}>
                <summary className="cursor-pointer list-none flex justify-between items-center font-semibold">
                  {f.q}
                  <span className="text-[var(--ft-gold)] group-open:rotate-45 transition-transform text-xl leading-none font-light">+</span>
                </summary>
                <p className="mt-4 text-sm text-[var(--ft-muted)] leading-relaxed">{resolveFaqAnswer(f.a, moqDescription)}</p>
              </details>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/faqs" className="ft-btn-ghost" data-testid="home-all-faqs">See all FAQs →</Link>
          </div>
        </div>
      </section>

      {/* ============================================================
          BOOK CONSULTATION — final CTA, white
          ============================================================ */}
      <section className="py-32 bg-white border-t border-[var(--ft-border)]" data-testid="home-cta">
        <div className="ft-container text-center max-w-3xl">
          <div className="ft-eyebrow mb-5">Book Consultation</div>
          <span className="ft-gold-rule-lg mx-auto" />
          <h2 className="mt-6 text-[clamp(2.25rem,4.5vw,3.5rem)] font-bold leading-[1.02] tracking-tight text-[var(--ft-black)] mb-6">
            Let&apos;s build your<br/>
            <span className="ft-serif font-medium" style={{ color: "var(--ft-gold)" }}>beverage brand</span>.
          </h2>
          <p className="text-[var(--ft-muted)] mb-10 text-lg max-w-xl mx-auto">
            Book a consultation with a FLOWTALIS specialist and receive a full launch scope tailored to your category, MOQ, and timeline.
          </p>
          <a href={CONSULTATION_URL} target="_blank" rel="noopener noreferrer" className="ft-btn-gold" data-testid="cta-book">
            Book a Consultation <ArrowRight size={16} />
          </a>
        </div>
      </section>

      <HomeClientShowcase />

      <HomeTurnkeyFeatured />

      {/* Pricing preview — mirrors /pricing so the customer sees the number
          without a click. Full list on /pricing links to Contact for a quote. */}
      <section className="py-24 bg-white" data-testid="home-pricing-preview">
        <div className="ft-container">
          <div className="max-w-2xl mb-12">
            <div className="ft-eyebrow mb-4">Transparent Pricing</div>
            <span className="ft-gold-rule-lg" />
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight text-[var(--ft-black)]">
              Bulk-discount pricing across every format.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PRICING_TIERS.map((tier) => <PricingCard key={tier.slug} tier={tier} />)}
          </div>
          <div className="mt-10 text-center">
            <Link to="/pricing" className="ft-btn-ghost" data-testid="home-view-pricing">
              View Full Pricing <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Home;
